/**
 * Install affordance (Phase 3, §5.6). A dismissible "App installieren" card
 * mounted in `AppShell`. On Chromium it triggers the captured
 * `beforeinstallprompt`; on iOS Safari (no event) it shows the static
 * "Teilen → Zum Home-Bildschirm" hint. It hides when already installed and its
 * dismissal persists a boolean in `sessionStorage` (a UI preference — never
 * finance data).
 */
import { useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { usePwaInstall } from '../context/PwaInstallContext.tsx';

const DISMISS_KEY = 'pwa-install-dismissed';

function wasDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const { canInstall, isIOS, isStandalone, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(wasDismissed);

  const showNative = canInstall && !isStandalone;
  const showIOSHint = isIOS && !canInstall && !isStandalone;

  if (dismissed || (!showNative && !showIOSHint)) return null;

  function dismiss() {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // sessionStorage may be unavailable (private mode) — dismissal then only
      // lasts for this render tree, which is acceptable for a UI hint.
    }
  }

  return (
    <div className="fixed inset-x-0 bottom-24 z-40 flex justify-center px-4 md:inset-x-auto md:bottom-4 md:right-4 md:justify-end">
      <div className="flex w-full max-w-sm items-start gap-3 rounded-lg bg-white p-4 shadow-lg ring-1 ring-slate-900/5 dark:bg-slate-900 dark:ring-white/10">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600 dark:bg-sky-950 dark:text-sky-300">
          {showIOSHint ? <Share className="h-5 w-5" /> : <Download className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            App installieren
          </p>
          {showIOSHint ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Tippe auf „Teilen“ und dann „Zum Home-Bildschirm“, um Finance Tracker zu installieren.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Installiere Finance Tracker für schnellen Zugriff vom Home-Bildschirm.
              </p>
              <button
                type="button"
                onClick={() => void promptInstall()}
                className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-3 py-1.5 text-[0.8125rem] font-semibold text-white transition-colors duration-150 hover:bg-sky-700 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:bg-sky-500 dark:hover:bg-sky-400 dark:focus-visible:outline-sky-500"
              >
                <Download className="h-4 w-4" />
                Installieren
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hinweis schließen"
          className="shrink-0 rounded-md p-1 text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
